import { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import {
  Box, Chip, CircularProgress, IconButton, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography, InputAdornment,
} from '@mui/material';
import { ArrowBack, Clear, Refresh, Search } from '@mui/icons-material';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import AppDropdown from '../../common/app-dropdown';
import { ColorsConstants } from '../../cssUtils/constants';
import { useAuth } from '../../hooks/AuthProvider';
import { CTWA_AUDIT_LOGS } from '../../config/urls';
import { formatWithTzLabel } from '../../utils/dateUtils';

// ── Constants ──────────────────────────────────────────────────────────────────

const ACTION_META = {
  CAMPAIGN_CREATED:   { label: 'Campaign Created',   bg: '#EFF6FF', color: '#1D4ED8' },
  CAMPAIGN_PUBLISHED: { label: 'Published',           bg: '#DCFCE7', color: '#16A34A' },
  CAMPAIGN_PAUSED:    { label: 'Paused',              bg: '#FEF9C3', color: '#CA8A04' },
  CAMPAIGN_RESUMED:   { label: 'Resumed',             bg: '#DCFCE7', color: '#16A34A' },
  DRAFT_SAVED:        { label: 'Draft Saved',         bg: '#F3F4F6', color: '#6B7280' },
  DRAFT_UPDATED:      { label: 'Draft Updated',       bg: '#F3F4F6', color: '#6B7280' },
};

const ACTION_FILTER_OPTIONS = [
  { label: 'All Actions',       value: '' },
  { label: 'Campaign Created',  value: 'CAMPAIGN_CREATED' },
  { label: 'Published',         value: 'CAMPAIGN_PUBLISHED' },
  { label: 'Paused',            value: 'CAMPAIGN_PAUSED' },
  { label: 'Resumed',           value: 'CAMPAIGN_RESUMED' },
  { label: 'Draft Saved',       value: 'DRAFT_SAVED' },
  { label: 'Draft Updated',     value: 'DRAFT_UPDATED' },
];

const PAGE_SIZE = 50;

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDateTime = (d) => d ? formatWithTzLabel(d, 'DD MMM YYYY, HH:mm') : '—';

const TH = ({ children, minWidth, align = 'left' }) => (
  <TableCell
    align={align}
    sx={{
      fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap',
      backgroundColor: ColorsConstants.primaryBlue60,
      color: ColorsConstants.primaryBlue100,
      minWidth,
    }}
  >
    {children}
  </TableCell>
);

const ActionChip = ({ action }) => {
  const meta = ACTION_META[action] || { label: action, bg: '#F3F4F6', color: '#6B7280' };
  return (
    <Chip
      label={meta.label}
      size="small"
      sx={{ height: 22, fontSize: 11, fontWeight: 700, backgroundColor: meta.bg, color: meta.color, borderRadius: 1 }}
    />
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

const CTWAAuditLog = ({ onBack }) => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem('tenant');

  const [logs, setLogs]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [offset, setOffset]     = useState(0);

  const [searchQuery, setSearchQuery]   = useState('');
  const [actionFilter, setActionFilter] = useState('');

  const fetchLogs = useCallback(async (newOffset = 0) => {
    setLoading(true);
    try {
      const token = await getValidToken();
      const params = new URLSearchParams({ limit: PAGE_SIZE, offset: newOffset });
      if (actionFilter) params.set('action', actionFilter);
      const res = await fetch(`${CTWA_AUDIT_LOGS}?${params}`, {
        headers: { Authorization: `Bearer ${token}`, 'X-Tenant-Id': tenant },
      });
      const data = await res.json();
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setOffset(newOffset);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [actionFilter, getValidToken, tenant]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchLogs(0); }, [fetchLogs]);

  const displayed = searchQuery.trim()
    ? logs.filter(
        (l) =>
          (l.campaign_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
          (l.campaign_id   || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
          (l.user_email    || '').toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : logs;

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          {onBack && (
            <IconButton onClick={onBack} size="small" sx={{ mr: 0.5 }}>
              <ArrowBack fontSize="small" />
            </IconButton>
          )}
          <Box>
            <Typography variant="h6" fontWeight={700} color={ColorsConstants.primaryBlue100}>
              Audit Logs
            </Typography>
            <Typography variant="body2" color={ColorsConstants.primaryGray50} sx={{ mt: 0.25 }}>
              All CTWA user actions — campaign creation, publish, pause / resume, drafts
            </Typography>
          </Box>
        </Stack>
        <Tooltip title="Refresh">
          <IconButton onClick={() => fetchLogs(offset)} size="small" disabled={loading}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Filter bar */}
      <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by campaign or user…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: { sm: 260 }, '& .MuiOutlinedInput-root': { height: 40, borderRadius: '8px', fontSize: 13 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 16, color: ColorsConstants.primaryGray40 }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')} edge="end">
                  <Clear sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <AppDropdown
          endIcon={<KeyboardArrowDownOutlinedIcon />}
          menuItems={ACTION_FILTER_OPTIONS.map(o => ({ label: o.label, value: o.value }))}
          selectedValue={actionFilter}
          onMenuSelect={(v) => setActionFilter(v)}
        >
          {actionFilter
            ? ACTION_FILTER_OPTIONS.find(o => o.value === actionFilter)?.label
            : 'All Actions'}
        </AppDropdown>
      </Stack>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : displayed.length === 0 ? (
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 6, textAlign: 'center' }}>
          <Typography variant="body1" fontWeight={700} color={ColorsConstants.primaryGray60} sx={{ mb: 0.5 }}>
            No audit logs found
          </Typography>
          <Typography variant="body2" color={ColorsConstants.primaryGray40}>
            Actions like publish, pause, resume, and draft saves will appear here.
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 2, maxHeight: { sm: 480, md: 600 }, overflow: 'auto' }}
          >
            <Table size="small" stickyHeader sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TH minWidth={160}>Timestamp</TH>
                  <TH minWidth={150}>Action</TH>
                  <TH minWidth={200}>Campaign</TH>
                  <TH minWidth={220}>User</TH>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayed.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                        {fmtDateTime(log.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <ActionChip action={log.action} />
                    </TableCell>
                    <TableCell>
                      {log.campaign_name ? (
                        <>
                          <Typography variant="body2" fontWeight={600}>{log.campaign_name}</Typography>
                          {log.campaign_id && (
                            <Typography variant="caption" color={ColorsConstants.primaryGray40} sx={{ fontFamily: 'monospace' }}>
                              {log.campaign_id}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" color={ColorsConstants.primaryGray40}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{log.user_email || '—'}</Typography>
                      {log.user_name && (
                        <Typography variant="caption" color={ColorsConstants.primaryGray50}>{log.user_name}</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {!searchQuery && totalPages > 1 && (
            <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={1} sx={{ mt: 1.5 }}>
              <Typography variant="caption" color={ColorsConstants.primaryGray50}>
                Page {currentPage} of {totalPages} &nbsp;·&nbsp; {total} total
              </Typography>
              <IconButton
                size="small" disabled={offset === 0}
                onClick={() => fetchLogs(offset - PAGE_SIZE)}
              >
                <ArrowBack fontSize="small" />
              </IconButton>
              <IconButton
                size="small" disabled={offset + PAGE_SIZE >= total}
                onClick={() => fetchLogs(offset + PAGE_SIZE)}
              >
                <ArrowBack fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
              </IconButton>
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};

export default CTWAAuditLog;
